import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { TFunction } from 'i18next';

import { ColumnFiltersState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { Table, Column, PageSizeType, TextButton, Tooltip, Sort } from 'design-system-web';
import {
  InspectionInstanceStatus,
  InspectionInstanceListSortKey,
  SortDirection,
} from 'graphql-query-contracts';
import {
  useGetInspectionInstances,
  useInspectionsCustomNameEnabled,
} from 'react-hooks';
import { dateFormatter } from 'async-timing';
import { SIMPLE_DATE, SortDirectionEnum } from 'constants-activate';
import { ANALYTIC_KEYS } from 'constants-analytics';

import { H3 } from 'components/typography';
import { useGetAuthToken } from 'hooks/useGetAuthToken';
import { AnalyticsContext, UserDataContext } from 'lane-shared/contexts';

import { STATUS_TRANSLATION_KEYS } from '../constants';
import {
  hasActiveDashboardFilters,
  parseDashboardFilters,
  useInspectionsDashboardFilters,
} from '../hooks/useInspectionsDashboardFilters';
import ChannelAdminContext from '../../../pages/portal/admin/channel/ChannelAdminContext';
import { downloadInspectionReportPdf } from '../services/downloadInspectionReportPdf';

import styles from './styles.scss';

const COLUMN_ID_TO_SORT_KEY: Record<string, InspectionInstanceListSortKey> = {
  inspectionName: InspectionInstanceListSortKey.TemplateName,
  status: InspectionInstanceListSortKey.Status,
  startedAt: InspectionInstanceListSortKey.StartedAt,
  completedAt: InspectionInstanceListSortKey.CompletedAt,
};

const SORT_KEY_TO_COLUMN_ID: Partial<
  Record<InspectionInstanceListSortKey, string>
> = {
  [InspectionInstanceListSortKey.TemplateName]: 'inspectionName',
  [InspectionInstanceListSortKey.Status]: 'status',
  [InspectionInstanceListSortKey.StartedAt]: 'startedAt',
  [InspectionInstanceListSortKey.CompletedAt]: 'completedAt',
};

type InspectionInstanceRow = {
  id: string;
  inspectionName: string | null;
  customName: string | null;
  completedByName: string | undefined;
  status: InspectionInstanceStatus;
  startedAt: string | null;
  completedAt: string | null;
};

function mapInstancesToRows(
  instances: ReturnType<typeof useGetInspectionInstances>['instances']
): InspectionInstanceRow[] {
  return instances.map(instance => ({
    id: instance.id,
    inspectionName: instance.templateRef.templateName ?? null,
    customName: instance.customName ?? null,
    completedByName: instance.state.completedByUser?.name,
    status: instance.state.status,
    startedAt: instance.state.startedAt ?? null,
    completedAt: instance.state.completedAt ?? null,
  }));
}

const DEFAULT_PAGE_SIZE: PageSizeType = 50;

const TABLE_KEY = 'inspections.inspectionsDashboardTable';

function getEmptyMessage({
  loading,
  totalCount,
  hasActiveFilters,
  t,
}: {
  loading: boolean;
  totalCount: number;
  hasActiveFilters: boolean;
  t: TFunction;
}): string | undefined {
  if (loading || totalCount !== 0) {
    return undefined;
  }
  return hasActiveFilters
    ? t('web.admin.inspections.dashboard.noFilterResults')
    : t('web.admin.inspections.dashboard.emptyState');
}

export function InspectionsDashboard() {
  const { t } = useTranslation();
  const analytics = useContext(AnalyticsContext);
  const { user } = useContext(UserDataContext);
  const { channel, timeZone } = useContext(ChannelAdminContext);
  const getAuthToken = useGetAuthToken();
  const isCustomNameEnabled = useInspectionsCustomNameEnabled();

  const channelId = channel?._id ?? '';

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<PageSizeType>(DEFAULT_PAGE_SIZE);
  const [downloadingInstanceId, setDownloadingInstanceId] = useState<
    string | null
  >(null);
  const reportDownloadInFlightRef = useRef(false);

  const [listSort, setListSort] = useState({
    key: InspectionInstanceListSortKey.CompletedAt,
    direction: SortDirection.Desc,
  });

  const [activeFilters, setActiveFilters] = useState<ColumnFiltersState>([]);

  const filterValues = useMemo(
    () => parseDashboardFilters(activeFilters),
    [activeFilters]
  );

  const hasActiveFilters = useMemo(
    () => hasActiveDashboardFilters(filterValues),
    [filterValues]
  );

  const { instances, loading, totalCount } = useGetInspectionInstances({
    channelId,
    start: page * pageSize,
    perPage: pageSize,
    sortBy: listSort,
    status: filterValues.status,
    completedBy: filterValues.completedBy,
    templateNameSearch: filterValues.templateNameSearch,
    startedAtFrom: filterValues.startedOn?.startDate?.toISOString(),
    startedAtTo: filterValues.startedOn?.endDate?.toISOString(),
    completedAtFrom: filterValues.completedOn?.startDate?.toISOString(),
    completedAtTo: filterValues.completedOn?.endDate?.toISOString(),
  });

  const { filters } = useInspectionsDashboardFilters(channelId, timeZone);

  const handlePageSizeChange = (newPageSize: PageSizeType) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleFilterChange = useCallback((filters: ColumnFiltersState) => {
    setActiveFilters(filters);
    setPage(0);
  }, []);
  const tableSort: Sort = useMemo(
    () => ({
      id: SORT_KEY_TO_COLUMN_ID[listSort.key] ?? 'completedAt',
      direction:
        listSort.direction === SortDirection.Desc
          ? SortDirectionEnum.Desc
          : SortDirectionEnum.Asc,
    }),
    [listSort]
  );

  const handleSortChange = useCallback((sort: Sort) => {
    if (!('id' in sort) || !sort.id) {
      setListSort({
        key: InspectionInstanceListSortKey.CompletedAt,
        direction: SortDirection.Desc,
      });
      return;
    }

    const key = COLUMN_ID_TO_SORT_KEY[String(sort.id)];
    if (!key) {
      return;
    }

    setListSort({
      key,
      direction:
        sort.direction === SortDirectionEnum.Desc
          ? SortDirection.Desc
          : SortDirection.Asc,
    });
  }, []);

  const handleReportDownload = useCallback(
    async (row: InspectionInstanceRow) => {
      if (row.status !== InspectionInstanceStatus.Completed) {
        return;
      }
      if (reportDownloadInFlightRef.current) {
        return;
      }

      reportDownloadInFlightRef.current = true;
      setDownloadingInstanceId(row.id);

      window.Alert.loading({
        title: t('web.admin.inspections.table.reportDownloadPreparing'),
        disableCloseOnBackgroundClick: true,
      });

      try {
        await downloadInspectionReportPdf({
          instanceId: row.id,
          getAuthToken,
        });
        if (user?._id && channel?._id) {
          analytics.track(
            ANALYTIC_KEYS.ANALYTIC_INSPECTIONS_ADMIN_REPORT_DOWNLOADED,
            {
              userId: user._id,
              channelId: channel._id,
              inspectionInstanceId: row.id,
            }
          );
        }
      } catch {
        await window.Toast.show(
          t('web.admin.inspections.table.reportDownloadErrorToast')
        );
      } finally {
        reportDownloadInFlightRef.current = false;
        setDownloadingInstanceId(null);
        setTimeout(() => window.Alert.hide(), 500);
      }
    },
    [analytics, channel, getAuthToken, t, user]
  );

  const rows = mapInstancesToRows(instances);

  const tableKey = isCustomNameEnabled ? `${TABLE_KEY}-custom-name` : TABLE_KEY;

  const columns: Column<InspectionInstanceRow>[] = [
    {
      key: 'inspectionName',
      header: t('web.admin.inspections.table.inspectionName'),
    },
    ...(isCustomNameEnabled
      ? [
          {
            key: 'customName',
            header: t('web.admin.inspections.table.customName'),
            disableSorting: true,
            renderCell: (_cell: unknown, row: InspectionInstanceRow) =>
              row.customName || '-',
          },
        ]
      : []),
    {
      key: 'completedByName',
      header: t('web.admin.inspections.table.completedBy'),
      disableSorting: true,
      renderCell: (_cell: unknown, row: InspectionInstanceRow) =>
        row.completedByName ?? t('web.admin.inspections.table.incomplete'),
    },
    {
      key: 'status',
      header: t('web.admin.inspections.table.status'),
      renderCell: (_cell: unknown, row: InspectionInstanceRow) =>
        t(STATUS_TRANSLATION_KEYS[row.status]),
    },
    {
      key: 'startedAt',
      header: t('web.admin.inspections.table.startedOn'),
      renderCell: (_cell: unknown, row: InspectionInstanceRow) =>
        row.startedAt
          ? dateFormatter(row.startedAt, SIMPLE_DATE, timeZone)
          : '',
    },
    {
      key: 'completedAt',
      header: t('web.admin.inspections.table.completedOn'),
      renderCell: (_cell: unknown, row: InspectionInstanceRow) =>
        row.completedAt
          ? dateFormatter(row.completedAt, SIMPLE_DATE, timeZone)
          : '',
    },
    {
      key: 'report',
      header: t('web.admin.inspections.table.report'),
      disableSorting: true,
      renderCell: (_cell: unknown, row: InspectionInstanceRow) => {
        const isCompleted = row.status === InspectionInstanceStatus.Completed;
        const isGloballyBusy = downloadingInstanceId !== null;
        const canDownload = isCompleted && !isGloballyBusy;

        const downloadControl = (
          <TextButton
            padding="none"
            disabled={!canDownload}
            onClick={() => {
              handleReportDownload(row).catch(() => {});
            }}
          >
            {t('web.admin.inspections.table.reportDownload')}
          </TextButton>
        );

        if (!isCompleted) {
          return (
            <Tooltip
              placement="top"
              TooltipComponent={t(
                'web.admin.inspections.table.reportDownloadDisabledTooltip'
              )}
            >
              {downloadControl}
            </Tooltip>
          );
        }

        return downloadControl;
      },
    },
  ];

  return (
    <div className={styles.inspectionsDashboard}>
      <div className={styles.titleWrapper}>
        <H3>{t('web.admin.inspections.dashboard.title')}</H3>
      </div>
      <Table
        key={tableKey}
        columns={columns}
        data={rows}
        isLoading={loading}
        emptyMessage={getEmptyMessage({
          loading,
          totalCount,
          hasActiveFilters,
          t,
        })}
        sorting={tableSort}
        onSortChange={handleSortChange}
        showColumnVisibility
        tableKey={tableKey}
        pagination="server"
        totalRows={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
        filters={filters}
        activeFilters={activeFilters}
        onFilterChange={handleFilterChange}
      />
    </div>
  );
}